import { useEffect, useState } from "react";
import styled from "styled-components";
import Avatar from "../../assets/pages/landing/AIav.png";

const MIRA_LABELS = [
  { language: "en", label: "Ask MIRA" },
  { language: "as", label: "মায়ৰাক সোধক" },
  { language: "bn", label: "মায়রাকে জিজ্ঞাসা করুন" },
  { language: "brx", label: "मायरासो सोध" },
  { language: "doi", label: "मायरा गी पुछो" },
  { language: "gu", label: "માયરાને પૂછો" },
  { language: "hi", label: "मायरा से पूछिए" },
  { language: "kn", label: "ಮಾಯ್ರಾಗೆ ಕೇಳಿ" },
  { language: "kok", label: "मायराक विचारात" },
  { language: "ks", label: "مایرا سۭتھ پٲچھو" },
  { language: "mai", label: "मायरा सँ पूछू" },
  { language: "ml", label: "മായ്റയോട് ചോദിക്കൂ" },
  { language: "mni", label: "মায়রাগী ৱারী" },
  { language: "mr", label: "मायराला विचारा" },
  { language: "ne", label: "मायरालाई सोध्नुहोस्" },
  { language: "or", label: "ମାୟରାଙ୍କୁ ପଚାରନ୍ତୁ" },
  { language: "pa", label: "ਮਾਇਰਾ ਨੂੰ ਪੁੱਛੋ" },
  { language: "sa", label: "मायरां पृच्छतु" },
  { language: "sat", label: "ᱢᱟᱭᱨᱟ ᱠᱚ ᱠᱩᱞᱤ" },
  { language: "sd", label: "مائرا کان پڇو" },
  { language: "ta", label: "மாய்ராவிடம் கேளுங்கள்" },
  { language: "te", label: "మైరాను అడగండి" },
  { language: "ur", label: "مائرا سے پوچھیں" },
];

const getBaseLanguage = (languageCode = "en") =>
  languageCode.toLowerCase().replace(/_/g, "-").split("-")[0] || "en";

const getInitialLanguage = () => {
  if (typeof window === "undefined") {
    return "en";
  }

  try {
    return getBaseLanguage(
      window.sessionStorage.getItem("selectedLang") ||
        window.localStorage.getItem("preferredLanguage") ||
        document.documentElement.lang ||
        "en",
    );
  } catch (error) {
    return getBaseLanguage(document.documentElement.lang || "en");
  }
};

const getMiraLabel = (languageCode) => {
  const language = getBaseLanguage(languageCode);

  return (
    MIRA_LABELS.find((entry) => entry.language === language)?.label ||
    MIRA_LABELS.find((entry) => entry.language === "en").label
  );
};

export const ChatBotAvatarCTA = ({ expanded = false, onOpen, chatOpen = false }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      setLanguage(getBaseLanguage(event.detail?.languageCode));
    };

    window.addEventListener("bhashini-language-change", handleLanguageChange);

    return () => {
      window.removeEventListener(
        "bhashini-language-change",
        handleLanguageChange,
      );
    };
  }, []);

  const miraLabel = getMiraLabel(language);
  const accessibleLabel = `${miraLabel} - NMDFC Virtual Assistant`;

  if (!onOpen) {
    return (
      <CTAWrapper data-chatbot-avatar aria-hidden="true">
        <AvatarImage
          $expanded={expanded}
          src={Avatar}
          alt=""
          aria-hidden="true"
        />
        <AskButtonBadge
          data-chatbot-label
          $expanded={expanded}
          className="dont-translate bhashini-skip-translation bhashini-skip-mutation"
          lang={language}
          aria-hidden="true"
        >
          {miraLabel}
        </AskButtonBadge>
      </CTAWrapper>
    );
  }

  return (
    <LauncherButton
      data-chatbot-avatar
      type="button"
      onClick={onOpen}
      aria-label={accessibleLabel}
      aria-haspopup="dialog"
      aria-expanded={chatOpen}
      title={accessibleLabel}
    >
      <AvatarImage
        $expanded={expanded}
        src={Avatar}
        alt=""
        aria-hidden="true"
      />
      <AskButtonBadge
        data-chatbot-label
        $expanded={expanded}
        className="dont-translate bhashini-skip-translation bhashini-skip-mutation"
        lang={language}
        aria-hidden="true"
      >
        {miraLabel}
      </AskButtonBadge>
    </LauncherButton>
  );
};

export const ChatbotHeader = styled.div`
  height: 64px;
  flex-shrink: 0;
  position: relative;
  z-index: 300;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 0 20px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 40px;
    padding: 6px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.15s;

    &:hover {
      background-color: rgba(0, 80, 72, 0.08);
      transform: scale(1.05);
    }

    &:focus-visible {
      outline: 2px solid #005048;
      outline-offset: 2px;
    }
  }

  @media (max-width: 480px), (max-height: 680px) {
    height: 52px;
    padding: 0 14px;
  }
`;

const CTAWrapper = styled.div`
  position: relative;
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 0;
  pointer-events: none;
  max-width: calc(100vw - 32px);
`;

const LauncherButton = styled.button`
  position: relative;
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  pointer-events: auto;
  max-width: calc(100vw - 32px);
  border-radius: 999px;
  transition: transform 0.2s ease, filter 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    filter: drop-shadow(0 6px 16px rgba(0, 80, 72, 0.22));
  }

  &:focus-visible {
    outline: 3px solid #005048;
    outline-offset: 4px;
    border-radius: 24px;
  }
`;

const AvatarImage = styled.img`
  display: block;
  width: ${({ $expanded }) => ($expanded ? "400px" : "200px")};
  height: auto;
  position: relative;
  z-index: 2;
  max-width: 100%;
  margin: 0;
  border-top-left-radius: ${({ $expanded }) => ($expanded ? "140px" : "80px")};
  border-top-right-radius: ${({ $expanded }) => ($expanded ? "140px" : "80px")};
  transition: all 0.3s ease;
  user-select: none;

  @media (max-width: 1024px) {
    width: ${({ $expanded }) => ($expanded ? "min(400px, 80vw)" : "180px")};
  }

  @media (max-width: 767px) {
    width: ${({ $expanded }) => ($expanded ? "min(320px, 80vw)" : "140px")};
  }
`;

const AskButtonBadge = styled.span`
  position: absolute;
  z-index: 3;
  left: 14%;
  top: 74%;
  width: 79%;
  min-height: 14%;
  height: auto;
  box-sizing: border-box;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  text-align: center;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.15;
  overflow: visible;
  pointer-events: none;
  background: #005048;
  color: #ffffff;
  font-weight: 700;
  font-family: inherit;
  letter-spacing: 0.2px;
  font-size: ${({ $expanded }) => ($expanded ? "18px" : "13px")};

  @media (max-width: 767px) {
    font-size: ${({ $expanded }) => ($expanded ? "14px" : "11px")};
  }
`;
